package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Desktop/web top navigation bar (Stitch "…(Desktop)" screens): a teal header with the brand on the
 * start side, the same destinations as the mobile bottom bar laid out horizontally (active tab in
 * saffron with a gold underline), and an optional trailing action slot (e.g. notifications). Shown
 * only on Expanded widths; narrow windows keep [AppBottomNavBar]. Copy-free — [brand] and each item's
 * label are passed in already localized.
 */
@Composable
fun AppTopNavBar(
    brand: String,
    items: List<AppBottomNavItem>,
    selectedRoute: String,
    onSelect: (AppBottomNavItem) -> Unit,
    modifier: Modifier = Modifier,
    /** Optional logo slot rendered before the wordmark (e.g. [BrandMark] sized to `topNavBrandMark`). */
    brandMark: (@Composable () -> Unit)? = null,
    trailing: (@Composable () -> Unit)? = null,
) {
    Box(
        modifier = modifier
            .fillMaxWidth()
            .background(AppTheme.colors.brandBand)
            // Inset before height: the teal background extends under the status bar, the content doesn't.
            .statusBarsPadding()
            .height(AppTheme.sizes.topNavBarHeight),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier
                // widthIn BEFORE fillMaxWidth — the reverse order pins the row to the window width
                // and the cap is ignored (see Layout.kt), which stretched the bar edge-to-edge.
                .widthIn(max = AppTheme.sizes.contentMaxWidth)
                .fillMaxWidth()
                .padding(horizontal = AppTheme.spacing.xl),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xl),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                if (brandMark != null) brandMark()
                Text(
                    text = brand,
                    style = AppTheme.typography.headlineLg,
                    color = AppTheme.colors.onBrandBand,
                )
            }
            Row(
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                items.forEach { item ->
                    TopNavItem(
                        label = item.label,
                        selected = item.route == selectedRoute,
                        onClick = { onSelect(item) },
                        modifier = item.modifier,
                    )
                }
            }
            Spacer(Modifier.weight(1f))
            if (trailing != null) trailing()
        }
    }
}

@Composable
private fun TopNavItem(label: String, selected: Boolean, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Column(
        // IntrinsicSize.Max sizes the column to the label width so the underline matches the text
        // (not the whole row — a plain fillMaxWidth here would make the active tab eat all the space).
        modifier = modifier
            .width(IntrinsicSize.Max)
            .selectable(selected = selected, role = Role.Tab, onClick = onClick),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xxs),
    ) {
        Text(
            text = label,
            style = AppTheme.typography.bodyMd,
            color = if (selected) AppTheme.colors.secondary else AppTheme.colors.onBrandBand,
        )
        // Gold underline marks the active destination (Stitch border-b-2 border-secondary-fixed).
        Box(
            modifier = Modifier
                .height(AppTheme.sizes.topNavActiveUnderline)
                .fillMaxWidth()
                .background(if (selected) AppTheme.colors.secondary else Color.Transparent),
        )
    }
}
